import { FC } from "react";
import {
  AbstractModelFactory,
  AbstractReactFactory,
  GenerateWidgetEvent,
} from "@projectstorm/react-canvas-core";
import {
  DiagramEngine,
  LinkModel,
  NodeModel,
  NodeModelGenerics,
  PortModelAlignment,
} from "@projectstorm/react-diagrams-core";
import {
  DefaultLinkModel,
  DefaultPortModel,
  RightAngleLinkModel,
} from "@projectstorm/react-diagrams";
import { useTranslation } from "react-i18next";
import {
  DiagramStartEndWrapper,
  PortWidgetWrapperBottom,
  StyledPortWidget,
} from "./DiagramStyles";
import { store } from "../../../store";
import { selectDiagramSettings } from "../../../store/diagramSettings";

const NAME = "start";

class Factory extends AbstractReactFactory<DiagramStartNode, DiagramEngine> {
  constructor() {
    super(NAME);
  }

  generateReactWidget(
    event: GenerateWidgetEvent<DiagramStartNode>
  ): JSX.Element {
    return <Widget engine={this.engine} node={event.model} />;
  }

  generateModel() {
    return new DiagramStartNode({});
  }
}

interface NodeProps {
  disableClick?: boolean;
  notFound?: boolean;
  highlight?: boolean;
  isLocked?: boolean;
}

export class DiagramStartNode extends NodeModel<NodeModelGenerics> {
  constructor(data: NodeProps) {
    super({
      type: NAME,
      extras: data,
    });
    this.addPort(new Port());
    this.setLocked(data.isLocked);
  }

  myPort() {
    return this.getPort(PortModelAlignment.BOTTOM) as Port;
  }

  myData() {
    return this.options.extras as NodeProps;
  }
}

interface WidgetProps {
  node: DiagramStartNode;
  engine: DiagramEngine;
}

const Widget: FC<WidgetProps> = ({ node, engine }) => {
  const { t } = useTranslation();
  const port = node.myPort();
  const data = node.myData();

  return (
    <DiagramStartEndWrapper
      highlight={!!data.highlight}
      canClick={!data.disableClick}
      notFound={!!data.notFound}
    >
      {t("diagram.start")}
      <PortWidgetWrapperBottom>
        <StyledPortWidget port={port} engine={engine} />
      </PortWidgetWrapperBottom>
    </DiagramStartEndWrapper>
  );
};

class Port extends DefaultPortModel {
  constructor() {
    super({
      type: NAME,
      name: PortModelAlignment.BOTTOM,
      alignment: PortModelAlignment.BOTTOM,
      in: false,
    });
  }

  createLinkModel(factory?: AbstractModelFactory<LinkModel>): LinkModel {
    if (factory) {
      return factory.generateModel({});
    }

    const diagramSettings = selectDiagramSettings(store.getState());
    if (diagramSettings.linkCurved) {
      return new DefaultLinkModel();
    }

    return new RightAngleLinkModel();
  }
}

export const DiagramStartRegister = (engine: DiagramEngine) => {
  engine.getNodeFactories().registerFactory(new Factory());
};
